import React, {
  FunctionComponent,
  useEffect,
  useState,
  Fragment,
  useRef,
  useCallback
} from 'react';
import { connect, ConnectedProps } from 'react-redux';
import i18n from 'i18n';
import './xccalist.scss';
import { useTranslation } from 'react-i18next';
import {
  AnchorButton,
  Button,
  Classes,
  Dialog,
  InputGroup,
  Intent,
  MenuItem,
  ProgressBar,
  Spinner,
  SpinnerSize
} from '@blueprintjs/core';
import {
  GridReadyEvent,
  SelectionChangedEvent
} from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { GridSettings } from 'data/types';
import * as afsSelectors from 'data/accessFunction/accessFunction.selectors';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as certStatusSelectors from 'data/certStatus/certStatus.selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as templateSelectors from 'data/template/template.selectors';
import * as globalSelectors from 'global/global-selectors';
import * as warrantActions from 'xcipio/warrants/warrant-actions';
import * as actions from 'data/jurisdictionArea/jurisdictionArea.actions';
import * as afsActions from 'data/accessFunction/accessFunction.actions';
import * as userActions from 'users/users-actions';
import { AppToaster, showSuccessMessage, showError } from 'shared/toaster';
import * as certStatusActions from 'data/certStatus/certStatus.actions';
import { AgGridReact } from '@ag-grid-community/react';
import { xccaColumnDefs } from 'routes/xcca/xcca.types';
import { Popover2, Tooltip2 } from '@blueprintjs/popover2';
import { DateRangePicker } from '@blueprintjs/datetime';
import MultiSelectCustom from 'shared/form/custom/MultiSelectCustom';
import { MultiselectTypeahead } from 'shared/form/custom/multiselectTypeahead';
import cloneDeep from 'lodash.clonedeep';
import { getTagOptions } from 'routes/AccessFunction/utils/accessFunctionTemplate.adapter';
import { CertStatus } from 'data/certStatus/certStatus.types';
import { ItemRenderer, Select } from '@blueprintjs/select';
import exportCertsCSV from 'routes/xcca/utils/exportCerts';

type RouterParams = {
  history: string;
};

type Option = {
  label: string;
  value: string;
};

interface XccaFilterCriteria {
  afTypes: string[];
  liTypes: string[];
  statuses: string[];
  expireDateInDays: number;
}

const LITYPE_OPTIONS = [
  { label: 'INI1', value: 'INI1' },
  { label: 'INI2', value: 'INI2' },
  { label: 'INI3', value: 'INI3' }
];

const STATUS_OPTIONS = [
  { label: 'UNKNOWN', value: 'UNKNOWN' },
  { label: 'CSRSENT', value: 'CSRSENT' },
  { label: 'PENDING_APPROVAL', value: 'PENDING_APPROVAL' },
  { label: 'APPROVED', value: 'APPROVED' },
  { label: 'DOWNLOADED', value: 'DOWNLOADED' },
  { label: 'ACTIVATED', value: 'ACTIVATED' },
  { label: 'REVOKED', value: 'REVOKED' },
  { label: 'EXPIRED', value: 'EXPIRED' },
];

const EXPIRE_DATE_OPTIONS = [
  { label: 'Within 30 days', value: '30' },
  { label: 'Within 60 days', value: '60' },
  { label: 'Within 90 days', value: '90' },
  { label: 'Within 1 year', value: '365' },
  { label: 'Within 2 years', value: '731' },
  { label: 'Within 3 years', value: '1096' },
];

const mapState = (state: RootState) => ({
  userName: authenticationSelectors.getAuthenticationUserName(state),
  privileges: authenticationSelectors.getAuthenticationPrivileges(state),
  beforeLogout: authenticationSelectors.getAuthenticationBeforeLogout(state),
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  allCertStatuses: certStatusSelectors.getAllCertStatus(state),
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(
    state
  ),
  accessFunctionTemplates: templateSelectors.getEnabledAccessFunctionFormTemplatesByKey(
    state
  )
});

const mapDispatch = {
  getGridSettings: userActions.getUserSettings,
  saveGridSettings: userActions.putUserSettings,
  getAllCertStatus: certStatusActions.getAllCertStatus,
  getTemplates: warrantActions.getAllTemplates
};
const PAGE_SIZE = 100;
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;

export type Props = PropsFromRedux & PropsFromRouter;

export const XccaList: FunctionComponent<Props> = ({
  userName,
  history,
  beforeLogout,
  privileges,
  deliveryFunctionId,
  allCertStatuses,
  getAllCertStatus,
  enabledAccessFunctions,
  getTemplates,
  accessFunctionTemplates
}: Props) => {
  const gridRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState(false);
  const { t } = useTranslation();
  const [afTypeMultiSelectOptions, setAfTypeMultiSelectOptions] = useState<MultiselectTypeahead[]>([]);
  const [afTypeAllOptions, setAfTypeAllOptions] = useState<MultiselectTypeahead[]>([]);

  const [liTypeMultiSelectOptions, setLiTypeMultiSelectOptions] = useState<MultiselectTypeahead[]>([]);
  const [liTypeAllOptions, setLiTypeAllOptions] = useState<MultiselectTypeahead[]>([]);

  const [statusMultiSelectOptions, setStatusMultiSelectOptions] = useState<MultiselectTypeahead[]>([]);
  const [statusAllOptions, setStatusAllOptions] = useState<MultiselectTypeahead[]>([]);

  const [selectedExpireDate, setSelectedExpireDate] = useState<Option>(EXPIRE_DATE_OPTIONS[0]);

  const [filteredCertStatuses, setFilteredCertStatuses] = useState<
    CertStatus[]
  >([]);

  useEffect(() => {
    getAllCertStatus(deliveryFunctionId);
    getTemplates();
  }, [deliveryFunctionId, getAllCertStatus, getTemplates]);

  useEffect(() => {
    if (enabledAccessFunctions && accessFunctionTemplates) {
      const tagOptions = getTagOptions(
        accessFunctionTemplates ?? {},
        enabledAccessFunctions ?? {}
      );
      const allOptions = tagOptions.map((x) => {
        return {
          label: x.label,
          value: x.value
        } as MultiselectTypeahead;
      });
      setAfTypeAllOptions(allOptions);
      setLiTypeAllOptions(LITYPE_OPTIONS);
      setStatusAllOptions(STATUS_OPTIONS);
    }

    if (allCertStatuses) {
      setFilteredCertStatuses(
        filterCerts(
          getFilterCriteria(
            afTypeMultiSelectOptions,
            liTypeMultiSelectOptions,
            statusMultiSelectOptions,
            selectedExpireDate.value
          )
        )
      );
    }
  }, [enabledAccessFunctions, accessFunctionTemplates, allCertStatuses]);

  const onGridReady = useCallback((grid: GridReadyEvent) => {
    // grid.api.setServerSideDatasource(getServerSideDataSource());
  }, []);

  const handleRefresh = () => {
    getAllCertStatus(deliveryFunctionId);
  };

  const handleExport = () => {
    setIsExporting(true);
    exportCertsCSV(filteredCertStatuses.slice());
    setIsExporting(false);
  };

  const handleAfTypeSelectChange = (
    name: string,
    value: { label: string; value: string }[]
  ) => {
    const selected = cloneDeep(value);
    setAfTypeMultiSelectOptions(selected);
    setFilteredCertStatuses(
      filterCerts(
        getFilterCriteria(
          selected,
          liTypeMultiSelectOptions,
          statusMultiSelectOptions,
          ''
        )
      )
    );
  };

  const handleLiTypeSelectChange = (
    name: string,
    value: { label: string; value: string }[]
  ) => {
    const selected = cloneDeep(value);
    setLiTypeMultiSelectOptions(selected);
    setFilteredCertStatuses(
      filterCerts(
        getFilterCriteria(
          afTypeMultiSelectOptions,
          selected,
          statusMultiSelectOptions,
          ''
        )
      )
    );
  }

  const handleStatusSelectChange = (
    name: string,
    value: { label: string; value: string }[]
  ) => {
    const selected = cloneDeep(value);
    setStatusMultiSelectOptions(selected);
    setFilteredCertStatuses(
      filterCerts(
        getFilterCriteria(
          afTypeMultiSelectOptions,
          liTypeMultiSelectOptions,
          selected,
          ''
        )
      )
    );
  }

  const handleExpireDateSelectChange = (item: Option) => {
    const selected = cloneDeep(item);
    setSelectedExpireDate(selected);
    setFilteredCertStatuses(
      filterCerts(
        getFilterCriteria(
          afTypeMultiSelectOptions,
          liTypeMultiSelectOptions,
          statusMultiSelectOptions,
          selected.value
        )
      )
    );
  }

  const getFilterCriteria = (
    afTypes: MultiselectTypeahead[],
    liTypes: MultiselectTypeahead[],
    statuses: MultiselectTypeahead[],
    expireDate: string,
  ): XccaFilterCriteria => {
    return {
      afTypes: (afTypes ?? []).map((x) => x.value),
      liTypes: (liTypes ?? []).map((x) => x.value),
      statuses: (statuses ?? []).map((x) => x.value),
      expireDateInDays: Number(expireDate)
    };
  };

  const filterCerts = (filterCriteria: XccaFilterCriteria) => {
    let ret = [...allCertStatuses];
    if (filterCriteria.afTypes.length > 0) {
      ret = ret.filter((cs) => {
        return filterCriteria.afTypes.includes(cs.AFTYPE);
      });
    }
    if (filterCriteria.liTypes.length > 0) {
      ret = ret.filter((cs) => {
        return filterCriteria.liTypes.includes(cs.LITYPE);
      });
    }
    if (filterCriteria.statuses.length > 0) {
      ret = ret.filter((cs) => {
        return filterCriteria.statuses.includes(cs.STATUS);
      });
    }
    if (filterCriteria.expireDateInDays) {
      const now = new Date();
      ret = ret.filter((cs) => {
        if (!cs.EXPIREDATE) return false;
        const expiryDate = new Date(cs.EXPIREDATE);
        if (Number.isNaN(expiryDate.getTime())) return false;
        const diffInDays = (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
        return diffInDays <= filterCriteria.expireDateInDays;
      });
    }
    return ret;
  };

  const renderOption: ItemRenderer<Option> = (item, { handleClick, modifiers }) => {
    if (!modifiers.matchesPredicate) return null;
    return (
      <MenuItem
        key={item.value}
        text={item.label}
        active={modifiers.active}
        disabled={modifiers.disabled}
        onClick={handleClick}
      />
    );
  };

  return (
    <div className="row xcca-dashboard-container" style={{ height: 'inherit' }}>
      <div className="formPanel">
        {/* <h2 className="formPanelTitle">XCCA Dashboard</h2> */}
      </div>
      <div className="col-md-12" style={{ height: 'inherit' }}>
        <div className="form-flex-row-css">
          <div className="xcca-filter-expireDate">
            <Select
              items={EXPIRE_DATE_OPTIONS}
              itemRenderer={renderOption}
              onItemSelect={(item) => handleExpireDateSelectChange(item)}
            >
              <Button
                text={
                  selectedExpireDate
                    ? selectedExpireDate.label
                    : 'Expires Within'
                }
                rightIcon="caret-down"
              />
            </Select>
          </div>
          <div className="xcca-filter-afType">
            <MultiSelectCustom
              items={afTypeAllOptions}
              fieldValue={afTypeMultiSelectOptions}
              name="afType"
              placeholder="AF Type"
              onItemSelect={handleAfTypeSelectChange}
              dataTestName=""
              disabled={false}
            />
          </div>
          <div className="xcca-filter-liType">
            <MultiSelectCustom
              className="xcca-filter-liType"
              items={liTypeAllOptions}
              fieldValue={liTypeMultiSelectOptions}
              name="litype"
              placeholder="LI Type"
              onItemSelect={handleLiTypeSelectChange}
              dataTestName=""
              disabled={false}
            />
          </div>
          <div className="xcca-filter-status">
            <MultiSelectCustom
              className="xcca-filter-status"
              items={statusAllOptions}
              fieldValue={statusMultiSelectOptions}
              name="status"
              placeholder="Status"
              onItemSelect={handleStatusSelectChange}
              dataTestName=""
              disabled={false}
            />
          </div>
          <div className="flex-spacer" />
          <Tooltip2 content="Export logs to file">
            <AnchorButton
              icon={
                isExporting ? <Spinner size={SpinnerSize.SMALL} /> : 'export'
              }
              intent={Intent.NONE}
              text="Export"
              disabled={isExporting}
              onClick={handleExport}
            />
          </Tooltip2>
          <div>
            <AnchorButton
              icon="refresh"
              intent={Intent.NONE}
              text="Refresh"
              onClick={handleRefresh}
            />
          </div>
        </div>
        <div className="ag-theme-balham common-grid-container">
          <AgGridReact
            ref={gridRef}
            columnDefs={xccaColumnDefs}
            pagination
            paginationPageSize={PAGE_SIZE}
            rowData={filteredCertStatuses}
            cacheBlockSize={PAGE_SIZE}
            onGridReady={onGridReady}
          />
        </div>
      </div>
    </div>
  );
};

export default withRouter(connector(XccaList));
